import classNames from 'classnames';
import React from 'react';

import {useI18n} from '@udemy/i18n';
import SuccessIcon from '@udemy/icons/dist/success.ud-icon';
import TickIcon from '@udemy/icons/dist/tick.ud-icon';
import {BlockList} from '@udemy/react-core-components';
import {TextSkeleton} from '@udemy/react-reveal-components';
import {Price} from '@udemy/shopping';
import {
    PlanPeriod,
    PlanPeriodStyle,
    SubscriptionCTA,
    SubscriptionDiscountRibbon,
} from '@udemy/subscription-browse';
import {usePersonalPlanCollectionStats} from '@udemy/subscription-browse';
import {ConsumerSubscriptionPlan} from '@udemy/subscription-graphql';
import {useFormatCurrency, useUDData} from '@udemy/ud-data';

import {UI_REGION} from 'udemy-django-static/js/browse/ui-regions';
import {formatNumber} from 'udemy-django-static/js/utils/numeral';
import {getNumericSiteStat} from 'udemy-django-static/js/utils/site-stats';

import {PersonalPlanLandingPageProps} from '../../personal-plan-landing-page-body.react-component';
import styles from './personal-plan-checklist.module.less';

export interface PersonalPlanChecklistProps extends PersonalPlanLandingPageProps {
    plan?: ConsumerSubscriptionPlan;
    planIds?: string[];
    isAnnualPlanEnabled?: boolean;
    isLoading?: boolean;
    isSubscriber: boolean;
    isCoursesPriceRangeLoading?: boolean;
    coursesPriceRange?: {
        min_price: Price;
        max_price: Price;
    };
}

export const PersonalPlanChecklist = React.forwardRef<HTMLDivElement, PersonalPlanChecklistProps>(
    (
        {
            plan,
            isCoursesPriceRangeLoading,
            coursesPriceRange,
            isPPLPRevamp,
        }: PersonalPlanChecklistProps,
        ref,
    ) => {
        const {gettext, interpolate} = useI18n();
        const {site_stats} = useUDData();
        const {formatCurrency} = useFormatCurrency();
        const collectionStatsData = usePersonalPlanCollectionStats();

        // Check if collection stats is loading
        const isCollectionStatsLoading = collectionStatsData?.isLoading ?? true;

        const contentCollectionStats =
            collectionStatsData && 'numberOfCourses' in collectionStatsData
                ? collectionStatsData
                : undefined;

        const courses = contentCollectionStats?.numberOfCourses ?? 0;
        const avgRating = contentCollectionStats?.courseAvgRating ?? 0;
        const practiceExercises = contentCollectionStats?.numberOfPracticeExercises ?? 0;
        const instructors = contentCollectionStats?.numberOfInstructors ?? 0;

        const personalPlanDiscount = plan?.selectedDiscount;
        const discountPriceText = personalPlanDiscount
            ? formatCurrency(personalPlanDiscount.subscriptionDiscountMonthlyPrice)
            : undefined;

        const subscriptionEntries = [
            {
                text: isCollectionStatsLoading ? (
                    <TextSkeleton lineCountPerParagraph={1} paragraphCount={1} />
                ) : (
                    interpolate(
                        gettext('%(courses)s+ professional and personal development courses'),
                        {courses: formatNumber(courses)},
                        true,
                    )
                ),
                icon: <TickIcon className={styles['checkmark']} label="" />,
            },
            {
                text: isCollectionStatsLoading ? (
                    <TextSkeleton lineCountPerParagraph={1} paragraphCount={1} />
                ) : (
                    interpolate(
                        gettext('%(avgRating)s/5 average rating'),
                        {
                            avgRating: formatNumber(avgRating),
                        },
                        true,
                    )
                ),
                icon: <TickIcon className={styles['checkmark']} label="" />,
            },
            {
                text: isCollectionStatsLoading ? (
                    <TextSkeleton lineCountPerParagraph={1} paragraphCount={1} />
                ) : (
                    interpolate(
                        gettext('%(practiceExercises)s+ practice exercises'),
                        {
                            practiceExercises: formatNumber(practiceExercises),
                        },
                        true,
                    )
                ),
                icon: <TickIcon className={styles['checkmark']} label="" />,
            },
            {
                text: isCollectionStatsLoading ? (
                    <TextSkeleton lineCountPerParagraph={1} paragraphCount={1} />
                ) : (
                    interpolate(
                        gettext('%(instructors)s+ top instructors'),
                        {
                            instructors: formatNumber(instructors),
                        },
                        true,
                    )
                ),
                icon: <TickIcon className={styles['checkmark']} label="" />,
            },
        ];

        const marketplaceEntries = [
            {
                text: interpolate(
                    gettext('%(courses)s+ professional and personal development courses'),
                    {courses: getNumericSiteStat('num_courses', {site_stats})},
                    true,
                ),
                icon: <TickIcon className={styles['checkmark']} label="" />,
            },
            {
                text: gettext('Pay as you go'),
                icon: <TickIcon className={styles['checkmark']} label="" />,
            },
        ];

        return (
            <div
                className={classNames(styles['personal-plan-checklist-wrapper'], {
                    [styles.revamp]: isPPLPRevamp,
                })}
                ref={ref}
            >
                <h1
                    className={classNames(
                        !isPPLPRevamp && 'ud-heading-serif-xxl',
                        styles['heading'],
                    )}
                    data-purpose="heading"
                >
                    {gettext('Choose a plan that works for you')}
                </h1>
                <div className={styles['checklist-options']}>
                    <div className={styles['personal-plan-checklist-option']}>
                        <div className={classNames('ud-text-md', styles['best-value-banner'])}>
                            <span className={styles['best-value-icon']}>
                                <SuccessIcon label="" aria-hidden="true" />
                            </span>
                            <span className={classNames('ud-text-xs', styles['best-value-text'])}>
                                {gettext('Best value')}
                            </span>
                        </div>
                        <div
                            className={classNames('ud-heading-serif-xl', styles['title'])}
                            data-purpose="title"
                        >
                            {gettext('Personal Plan')}
                        </div>
                        <div
                            className={classNames('ud-text-xs', styles['subtitle'])}
                            data-purpose="subtitle"
                        >
                            {gettext('Streamline your career goals')}
                        </div>
                        {personalPlanDiscount && (
                            <div className={styles['subscription-discount-ribbon']}>
                                <SubscriptionDiscountRibbon
                                    subscriptionDiscount={personalPlanDiscount}
                                />
                            </div>
                        )}
                        <div>
                            {plan?.isLoading ? (
                                <div className={styles['skeleton']}>
                                    <TextSkeleton lineCountPerParagraph={1} paragraphCount={1} />
                                </div>
                            ) : plan && 'id' in plan ? (
                                <div
                                    className={styles['subscription-text']}
                                    data-purpose="subscription-text"
                                >
                                    <PlanPeriod
                                        showStartingAtPrice={plan.isAnnualPlanEnabled}
                                        listPriceText={plan?.formattedMonthlyAmount}
                                        trialPeriodDays={plan.trialPeriodDays}
                                        discountPriceText={discountPriceText}
                                        style={
                                            personalPlanDiscount
                                                ? PlanPeriodStyle.PLAN_COMPARISON
                                                : undefined
                                        }
                                    />
                                </div>
                            ) : undefined}
                            <div
                                className={classNames('ud-text-xs', styles['purchase-subtitle'])}
                                data-purpose="subscription-subtitle"
                            >
                                {gettext('Billed monthly or annually. Cancel anytime.')}
                            </div>
                        </div>
                        <BlockList
                            className={styles['checklist-entries']}
                            padding={'tight'}
                            size={'small'}
                        >
                            {subscriptionEntries.map((item, index) => {
                                return (
                                    <BlockList.Item key={index} icon={item.icon}>
                                        {item.text}
                                    </BlockList.Item>
                                );
                            })}
                        </BlockList>
                        <SubscriptionCTA
                            className={styles['subscription-cta']}
                            plan={plan}
                            data-purpose="subscription-cta"
                            uiRegion={UI_REGION.PERSONAL_PLAN_CHECKLIST}
                            showSubtitle={false}
                        />
                    </div>
                    <div className={styles['marketplace-plan-checklist-option']}>
                        <div
                            className={classNames('ud-heading-serif-xl', styles['title'])}
                            data-purpose="title"
                        >
                            {gettext('Buy individual courses')}
                        </div>
                        <div
                            className={classNames('ud-text-xs', styles['subtitle'])}
                            data-purpose="subtitle"
                        >
                            {gettext('Learn anything')}
                        </div>
                        <div>
                            {isCoursesPriceRangeLoading ? (
                                <div className={styles['skeleton']}>
                                    <TextSkeleton lineCountPerParagraph={1} paragraphCount={1} />
                                </div>
                            ) : coursesPriceRange ? (
                                <div
                                    className={classNames('ud-text-md', styles['marketplace-text'])}
                                    data-purpose="marketplace-text"
                                >
                                    {interpolate(
                                        gettext('%(lowerBound)s-%(upperBound)s'),
                                        {
                                            lowerBound: formatCurrency(
                                                coursesPriceRange.min_price.amount,
                                                {
                                                    currency: coursesPriceRange.min_price.currency,
                                                    symbol: coursesPriceRange.min_price
                                                        .currency_symbol,
                                                },
                                            ),
                                            upperBound: formatCurrency(
                                                coursesPriceRange.max_price.amount,
                                                {
                                                    currency: coursesPriceRange.max_price.currency,
                                                    symbol: coursesPriceRange.max_price
                                                        .currency_symbol,
                                                },
                                            ),
                                        },
                                        true,
                                    )}
                                </div>
                            ) : undefined}
                            <div
                                className={classNames('ud-text-xs', styles['purchase-subtitle'])}
                                data-purpose="marketplace-subtitle"
                            >
                                {gettext('One time purchase')}
                            </div>
                        </div>
                        <BlockList className={styles['checklist-entries']} size={'small'}>
                            {marketplaceEntries.map((item, index) => {
                                return (
                                    <BlockList.Item key={index} icon={item.icon}>
                                        {item.text}
                                    </BlockList.Item>
                                );
                            })}
                        </BlockList>
                    </div>
                </div>
            </div>
        );
    },
);
